import { getExtensionValue, getReferenceString, resolveId } from "@medplum/core";
import {
  ChargeItem,
  Encounter,
  Identifier,
  Patient,
  Period,
  Procedure,
  Reference,
} from "@medplum/fhirtypes";

import { EncounterServicesStatusCode } from "@/billing/constants/encounter-services-status";
import { EncounterServicesStatusCodeValue } from "@/billing/types/encounter-services-status";
import {
  buildChargeItemPayload,
  resolveChargeItemPlaceOfService,
} from "@/billing/utils/build-charge-item-payload";
import { PaymentMethod } from "@/constants/billing";
import { Extensions } from "@/fhir/enums/extensions";
import { QuiltedHealthIdentifierSystems } from "@/fhir/enums/quilted-health";
import { formatDateInTimezone } from "@/utils/datetime";
import { getCodeableConceptCode } from "@/utils/medplum";

import { getEncounterServiceDateTimezone } from "./encounter-timezone";
import { toServiceDateTimezoneFormValue } from "./service-date-timezone-form";

/**
 * The state a charge is captured in: recorded against the encounter, not yet priced or sent. The
 * billing pipeline is what moves it on to `billable` and then `billed`.
 */
export const CAPTURED_CHARGE_ITEM_STATUS: ChargeItem["status"] = "planned";

/** Units a captured charge starts at when its Procedure carries none of its own. */
export const CAPTURED_CHARGE_ITEM_QUANTITY = 1;

/** The units recorded on a Procedure when it was charted, or the capture default when it has none. */
function resolveProcedureUnits(procedure: Procedure): number {
  const units = getExtensionValue(procedure, Extensions.ProcedureUnits);

  return typeof units === "number" ? units : CAPTURED_CHARGE_ITEM_QUANTITY;
}

/** A note recorded on a Procedure when it was charted, trimmed. Blank when it carries none. */
function resolveProcedureNote(procedure: Procedure, url: Extensions): string {
  const note = getExtensionValue(procedure, url);

  return typeof note === "string" ? note.trim() : "";
}

/**
 * The status a charge reaches when the biller sends the encounter's Services on.
 *
 * Not a cosmetic step. `billable` is the only status statement generation will put on a statement —
 * its pre-flight rejects anything else, and its flip to `billed` skips it — so a charge left
 * `planned` is invisible to billing however complete the encounter is. Sending is what makes it
 * visible.
 *
 * The statement bot names the same string `CHARGE_ITEM_UNBILLED`, after what the status means to a
 * statement: not billed yet. This one is named after what it means to a biller: sent. They are
 * deliberately not shared — the encounter's tab has no business importing a bot's internals.
 */
export const SENT_CHARGE_ITEM_STATUS: ChargeItem["status"] = "billable";

/**
 * The status a charge is voided with, which is what deleting a biller-section row writes.
 *
 * `entered-in-error` is FHIR's "this record should never have existed", so the charge stays on the
 * server and stops counting as something the encounter will be billed for. Every other status —
 * `aborted`, `not-billable`, `billed` — is a decision someone made about a real charge, which is why
 * a voided one is the only status the reads single out.
 *
 * Recognised in code rather than in a search: R4 defines no `status` search parameter for ChargeItem,
 * so `status:not=` on this resource matches nothing at all rather than excluding what it names.
 */
export const VOIDED_CHARGE_ITEM_STATUS: ChargeItem["status"] = "entered-in-error";

/**
 * The charges an encounter still counts toward billing, with voided ones removed.
 *
 * The single reader of what "active" means: both the rows the biller section lists and the
 * Location resolved from those charges are read off this same set, so a voided charge cannot
 * supply either one after it has been removed.
 */
export function getActiveChargeItems(chargeItems: ChargeItem[]): ChargeItem[] {
  return chargeItems.filter((chargeItem) => chargeItem.status !== VOIDED_CHARGE_ITEM_STATUS);
}

/**
 * Whether every active charge of an encounter has been sent on, which is the encounter's
 * `services-sent` state.
 *
 * Matches what `sendEncounterServices` means by `services-sent`: voided charges are ignored, and an
 * encounter with no active charge has no Services to be sent, so it is not `services-sent`.
 *
 * @param chargeItems Every charge the encounter has, voided ones included.
 * @returns True when there is at least one active charge and none is still `planned`.
 */
export function areAllEncounterServicesSent(chargeItems: ChargeItem[]): boolean {
  const services = getActiveChargeItems(chargeItems);

  return (
    services.length > 0 && services.every(({ status }) => status !== CAPTURED_CHARGE_ITEM_STATUS)
  );
}

/**
 * The `qh-encounter-services-status` code an encounter's charges call for right now.
 *
 * `services-sent` when every active charge has been sent on, `services-not-sent` while one is still
 * `planned`. An encounter with no active charge has no Services, which has no code of its own, so
 * it returns `undefined`.
 *
 * @param chargeItems Every charge the encounter has, voided ones included.
 */
export function resolveEncounterServicesStatus(
  chargeItems: ChargeItem[],
): EncounterServicesStatusCodeValue | undefined {
  if (areAllEncounterServicesSent(chargeItems)) return EncounterServicesStatusCode.Sent;

  const hasUnsentService = getActiveChargeItems(chargeItems).some(
    ({ status }) => status === CAPTURED_CHARGE_ITEM_STATUS,
  );

  return hasUnsentService ? EncounterServicesStatusCode.NotSent : undefined;
}

/**
 * Identifier system for the key every captured charge carries.
 *
 * Skipping already-charged procedures is a read-then-write, which leaves a window where two
 * overlapping captures both read nothing and both write. The key closes it: the write is a
 * conditional create on this identifier, so the second one matches the first instead of adding a
 * duplicate. The key is derived from the encounter and procedure ids rather than generated, so both
 * captures compute the same value.
 */
export const CAPTURED_CHARGE_ITEM_IDENTIFIER_SYSTEM =
  QuiltedHealthIdentifierSystems.ChargeCaptureIdentifier;

/**
 * A captured charge with its key stamped on.
 *
 * The builder returns only these, so the write never has to handle a charge it cannot key a
 * conditional create on.
 */
export type CapturedChargeItem = ChargeItem & { identifier: [Identifier & { value: string }] };

/** The key identifying one procedure's charge on one encounter. */
function buildCapturedChargeItemIdentifier(
  encounterId: string,
  procedureId: string,
): Identifier & { value: string } {
  return {
    system: CAPTURED_CHARGE_ITEM_IDENTIFIER_SYSTEM,
    value: `${encounterId}-${procedureId}`,
  };
}

/** The key a built charge carries, which its conditional create is matched on. */
export function getCapturedChargeItemKey(chargeItem: CapturedChargeItem): string {
  return chargeItem.identifier[0].value;
}

type BuildEncounterChargeItemsParams = {
  /**
   * The encounter's active procedures — the clinical side of what is being charged for. Retired
   * procedures are already excluded by the read that hands these over, so nothing here restates it.
   */
  procedures: Procedure[];
  /** What the encounter already has captured, so signing again cannot charge a procedure twice. */
  existingChargeItems: ChargeItem[];
  patientReference: Reference<Patient>;
  encounterReference: Reference<Encounter>;
  /** The window the work was documented over. Left off when the encounter carries no start. */
  occurrencePeriod?: Period;
  paymentMethod: PaymentMethod;
  /** Practice the fee definition is scoped to. Without one the charge points at the global fee. */
  practiceOrganizationId?: string;
  /**
   * Clinical provider Practitioner id stamped onto `ChargeItem.performer` so statement grouping
   * and Services-tab display read the provider from the charge, not only the Encounter.
   */
  performerPractitionerId?: string;
  /**
   * The Location every charge on this encounter already carries, so a newly captured one matches
   * instead of leaving the encounter's charges split across two values.
   */
  placeOfService?: string;
};

/**
 * The window a charge is captured over, taken from the encounter's own period.
 *
 * An encounter documented across more than one day carries both ends. A single-day one carries only
 * its start: an `end` on the same calendar day says nothing the start does not already say, and
 * billing reads a start-only period as "this happened on that date".
 *
 * The comparison runs in the encounter's service-date timezone rather than UTC, because a visit that
 * starts at 8pm Pacific is one day to the practice and two in UTC, and the practice's day is the one
 * that gets billed.
 *
 * @returns The period to record, or undefined when the encounter has no start to anchor it.
 */
export function resolveEncounterOccurrencePeriod(encounter: Encounter): Period | undefined {
  const start = encounter.period?.start;
  if (!start) return undefined;

  const end = encounter.period?.end;
  if (!end) return { start };

  const { code: timezone } = toServiceDateTimezoneFormValue(
    getEncounterServiceDateTimezone(encounter),
  );

  const spansOneDay = formatDateInTimezone(start, timezone) === formatDateInTimezone(end, timezone);

  return spansOneDay ? { start } : { start, end };
}

/**
 * Whether a Procedure was written for a biller-section row, which is a row that already has a
 * charge.
 *
 * A row edit keys its Procedure on the row's own ChargeItem, so the charge exists before the
 * Procedure does and is repointed at it straight after. Skipping already-charged procedures is only
 * as current as the charges the capture was handed, so without this a capture running in that gap
 * would create a second charge for a row that already has one. The key answers it from the resource
 * itself.
 */
function isBillerSectionRowProcedure(procedure: Procedure): boolean {
  return Boolean(
    procedure.identifier?.some(
      ({ system }) => system === QuiltedHealthIdentifierSystems.BillerSectionRowProcedureIdentifier,
    ),
  );
}

/** Every Procedure the given charges were captured from, as reference strings. */
function toChargedProcedureReferences(chargeItems: ChargeItem[]): Set<string> {
  return new Set(
    chargeItems.flatMap(({ supportingInformation }) =>
      (supportingInformation ?? [])
        .map(({ reference }) => reference)
        .filter((reference): reference is string => Boolean(reference)),
    ),
  );
}

/**
 * Builds the billing copy of an encounter's procedures: one ChargeItem per Procedure.
 *
 * A Service is not a wrapper resource — it is the set of ChargeItems sharing an Encounter `context` —
 * so the grouping is `context` alone and an encounter with N procedures produces N charges.
 * `supportingInformation` points back at the Procedure the charge came from. That is both the link
 * to the clinical record and what this skips on: a procedure already charged is left alone, so
 * signing an amendment captures only what was added since. The skip is only as current as the
 * charges it was handed, so each charge also carries the same encounter-and-procedure pairing as an
 * identifier — see {@link CAPTURED_CHARGE_ITEM_IDENTIFIER_SYSTEM} for what the write does with it.
 *
 * A procedure written for a biller-section row is skipped whatever the charges say, because that row
 * already has a charge. See {@link isBillerSectionRowProcedure}.
 *
 * Payload shape comes from the shared ChargeItem builder used by custom services; encounter capture
 * keeps modifiers on the Procedure (not stamped onto the ChargeItem).
 *
 * The Practitioner and Billing notes are the exception: they are copied onto the charge, because
 * the billing tab reads a row's notes off the charge rather than off the Procedure. See
 * {@link Extensions.ChargeItemBillerNotes}.
 */
export function buildEncounterChargeItems({
  procedures,
  existingChargeItems,
  patientReference,
  encounterReference,
  occurrencePeriod,
  paymentMethod,
  practiceOrganizationId,
  performerPractitionerId,
  placeOfService,
}: BuildEncounterChargeItemsParams): CapturedChargeItem[] {
  const chargedProcedures = toChargedProcedureReferences(existingChargeItems);
  const encounterId = resolveId(encounterReference);

  // Without an encounter there is nothing to capture against, and no encounter half of the key to
  // identify a charge by.
  if (!encounterId) return [];

  return procedures.reduce<CapturedChargeItem[]>((chargeItems, procedure) => {
    const procedureId = procedure.id;
    const procedureReference = getReferenceString(procedure);
    const code = procedure.code;
    const procedureCode = getCodeableConceptCode(code);

    // A procedure with no id cannot be referenced back to, and one with no code cannot be priced.
    if (!procedureId || !procedureReference || !code || !procedureCode) return chargeItems;
    if (chargedProcedures.has(procedureReference)) return chargeItems;
    if (isBillerSectionRowProcedure(procedure)) return chargeItems;

    // Added as it is built, so the same procedure listed twice still yields one charge.
    chargedProcedures.add(procedureReference);

    const identifier = buildCapturedChargeItemIdentifier(encounterId, procedureId);
    const chargeItem = buildChargeItemPayload({
      status: CAPTURED_CHARGE_ITEM_STATUS,
      code,
      subject: patientReference,
      paymentMethod,
      practiceOrganizationId,
      context: encounterReference,
      supportingInformation: [{ reference: procedureReference }],
      identifier: [identifier],
      quantity: resolveProcedureUnits(procedure),
      occurrencePeriod,
      performerPractitionerId,
      placeOfService,
      billerNotes: resolveProcedureNote(procedure, Extensions.ProcedureBillerNotes),
      providerNotes: resolveProcedureNote(procedure, Extensions.ProcedureProviderNotes),
    });

    chargeItems.push({
      ...chargeItem,
      identifier: [identifier],
    });

    return chargeItems;
  }, []);
}

/**
 * The Location an encounter's charges already carry, read off whichever existing ChargeItem has
 * one first.
 *
 * Every charge on one encounter is kept in sync with the same Location, so which one the value is
 * read from does not matter — only that a value already set is not lost when a new charge is built.
 *
 * @returns The shared value, or undefined when none of the given charges carry one.
 */
export function resolveEncounterChargeItemsLocation(chargeItems: ChargeItem[]): string | undefined {
  for (const chargeItem of chargeItems) {
    const placeOfService = resolveChargeItemPlaceOfService(chargeItem);
    if (placeOfService) return placeOfService;
  }

  return undefined;
}
